import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { matchPath, generatePath, useHistory, useLocation } from 'react-router-dom';
import { Button, HeaderBar, makeStyles, HeaderBarLogo } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import { spacing40 } from '@ellucian/react-design-system/core/styles/tokens';
import { routes, adminRoutes, homePageMap } from '../Helpers/Configuration';
import { useTheme } from '@mui/material';
import useRoot from '../RootContext/useRoot';
import Logo from './Logo';
import GlobalLocaleHeaderItems from '../GlobalLocale/GlobalLocaleHeaderItems';
import { createEDSTheme } from '@ellucian/react-design-system/core/themes';
const useStyles = makeStyles(theme => {
    const { eteTheme } = useRoot();
    const setBackgroundImage = (image, color) => ({
        backgroundImage: image ? `url(${image}) !important` : 'none',
        backgroundColor: image ? 'transparent !important' : `${color} !important`,
    });
    return {
        headerBarLogoRoot: {
            display: 'flex',
            backgroundColor: 'transparent !important',
        },
        root: {
            left: 0,
            position: 'fixed',
            top: 0,
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            [theme.breakpoints.up('md')]: setBackgroundImage(eteTheme?.bgimage, theme.palette.primary.main),
            [theme.breakpoints.only('sm')]: setBackgroundImage(eteTheme?.portrait_bgimage || eteTheme?.bgimage, theme.palette.primary.main),
            [theme.breakpoints.down('sm')]: setBackgroundImage(eteTheme?.small_portrait_bgimage || eteTheme?.portrait_bgimage || eteTheme?.bgimage, theme.palette.primary.main),
        },
        signOut: {
            display: 'flex',
        },
        username: {
            marginRight: spacing40,
            marginTop: '4px',
        },
        [theme.breakpoints.down('sm')]: {
            username: {
                display: 'none',
            },
        },
    };
});

const Header = props => {
    const intl = useIntl();
    const {
        showUtilityBar = true,
    } = props;
    const globalSettings = window?.PRELOADED_STATE?.globalSettings;
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const rootCtx = useRoot();
    const theme = useTheme();
    const isThemed = useMemo(() => {
        const lightTheme = createEDSTheme('light');
        return theme.palette.primary.main !== lightTheme.palette.primary.main;
    }, [createEDSTheme, theme]);
    const applicationRouteMatch = useMemo(() => {
        if (typeof history.location.pathname !== 'string') {
            return null;
        }
        return matchPath(history.location.pathname, {
            path: [
                routes.applicationForm,
                routes.applicationFormPageById,
                routes.applicationFormReviewPage,
                routes.applicationFormStart,
                routes.applicationFormSubmitPage,
                routes.applicationFormWithdrawnPage,
                routes.applicationFormSupplementalItemsPage,
                routes.applicationFormPayments,
                routes.applicationFormSupplementalForm,
                routes.applicationShare,
                routes.externalApplication,
                routes.externalApplicationPageById,
                routes.externalApplicationSubmitPage,
                routes.externalApplicationSupplementalItemsPage,
                routes.externalApplicationPayments,
                routes.externalApplicationwReviewPage
            ]
        });
    }, [history.location.pathname]);

    useEffect(() => {
        if (!applicationRouteMatch) {
            document.title = globalSettings?.applyPageTitle;
        }
    }, [applicationRouteMatch, globalSettings]);

    const homeRoute = useMemo(() => {
        if (window.PRELOADED_STATE?.user) {
            const adminMatch = matchPath(location.pathname, adminRoutes);
            return adminMatch ? routes.admin : routes.home;
        }
        else {
            const formMatch = matchPath(location.pathname, {
                path: Object.keys(homePageMap)
            });

            if (formMatch)
                return generatePath(homePageMap[formMatch.path], formMatch.params);
        }

        return routes.retrieval;
    }, [location.pathname]);

    const UtilityBar = useMemo(() => {
        if (!showUtilityBar) {
            return (
                <GlobalLocaleHeaderItems isThemed={isThemed} />
            );
        }
        else return (
            <>
                {window.location.pathname.includes('/preview/') &&
                    <GlobalLocaleHeaderItems isThemed={isThemed} />
                }
            </>
        );
    }, [showUtilityBar, classes]);
    const logoSrc = useMemo(() => {
        return rootCtx?.eteTheme?.logo ?? null;
    }, [rootCtx.eteTheme]);
    const logoOnClickCallback = () => {
        rootCtx.clearTheme();
        history.push(homeRoute);
    };
    const isLogoBtnDisabled = useMemo(() => {
        return (history.location.pathname === routes.totp || history.location.pathname === routes.totpReset || history.location.pathname === routes.totpRecovery);
    }, [history.location.pathname]);
    return (
        <HeaderBar
            classes={{ root: classes.root, logoRoot: classes.headerBarLogoRoot }}
            logo={
                <HeaderBarLogo classes={{ root: classes.headerBarLogoRoot }} style={{ background: 'transparent' }}
                >
                    <Button
                        onClick={logoOnClickCallback}
                        classes={{ root: classes.headerBarLogoRoot }}
                        variant="text"
                        disabled={isLogoBtnDisabled}
                        aria-label={intl.formatMessage({ id: 'institutionLogoAltText' })}
                    >
                        <Logo src={logoSrc} />
                    </Button>
                </HeaderBarLogo>
            }
            position="static"
            showSearch={false}
            UtilityBar={UtilityBar}
        />
    );
};
Header.propTypes = {
    showUtilityBar: PropTypes.bool,
};
export default Header;
